//  dedupeTransactions.js
//  One bank transaction is one row.
//
//  The sync pipeline guards this at the write end with an advisory lock, and
//  that lock's own comment names the failure it cannot close: RTDB REST has no
//  compare-and-set, so two runs landing inside the same read-then-set gap both
//  see no existing row for a transaction and both insert it, under two
//  different push ids. Observed in real data — pairs of rows carrying an
//  identical `bankTxId`, written under a second apart.
//
//  A duplicate is not a cosmetic problem. Downstream it is silently wrong in
//  several directions at once:
//
//    * budgets and spending totals count the purchase twice;
//    * detectTransferPairs matches ONE of two identical card repayments to the
//      cash leg and leaves the other unmatched, which in-flight.js then reads
//      as a payment posted on the card whose cash debit hasn't landed — a
//      phantom outflow projected against a balance that already paid it.
//
//  So the invariant is enforced here, at read time, for every consumer at
//  once. It is deliberately NOT a fix applied to stored data: deleting rows is
//  destructive and belongs behind an explicit, audited cleanup. This makes the
//  app correct while the duplicates are still on disk, and keeps it correct if
//  the write-side race ever fires again.
//
//  Only exact `bankTxId` collisions are collapsed. Two rows that merely look
//  alike — same merchant, amount and day — are left alone, because that shape
//  is also just what buying two coffees looks like.

import { BANK_TX_ID } from './dbKeys.js';

// Which copy survives. A row the user has categorized by hand carries an edit
// that the other copy does not, so it wins outright; discarding it would throw
// the edit away. Otherwise keep the one written first: Firebase push ids sort
// lexicographically by creation time, so the smallest key is the original and
// the later copy is the accident.
function preferred(a, b) {
  if (!!a.manualCategory !== !!b.manualCategory) return a.manualCategory ? a : b;
  return String(a.id) <= String(b.id) ? a : b;
}

/**
 * Collapse rows that share a bank transaction id.
 *
 * Input order is preserved, and the surviving row keeps the position of the
 * first copy seen so a sorted list stays sorted.
 *
 * @param {Array} transactions
 * @returns {Array} the same list with same-`bankTxId` duplicates removed
 */
export function dedupeTransactions(transactions) {
  if (!Array.isArray(transactions)) return [];

  const slotByBankId = new Map();
  const out = [];

  transactions.forEach((t) => {
    // Manual and CSV rows have no provider id. They are not deduped: there is
    // nothing authoritative to key on, and two hand-entered rows the same day
    // are far more likely to be two real purchases than one mistake.
    const bankId = t && t[BANK_TX_ID];
    if (!bankId) {
      out.push(t);
      return;
    }
    const slot = slotByBankId.get(bankId);
    if (slot === undefined) {
      slotByBankId.set(bankId, out.length);
      out.push(t);
      return;
    }
    out[slot] = preferred(out[slot], t);
  });

  return out;
}

/**
 * How many rows dedupeTransactions() would drop. Reporting only — used to
 * surface the write-side race rather than let it stay silent.
 */
export function duplicateCount(transactions) {
  if (!Array.isArray(transactions)) return 0;
  return transactions.length - dedupeTransactions(transactions).length;
}
